// main-news.ts
/**
 * main-news.js  —  Kili AI Assistant · News Segment Event Bindings
 *
 * Call initNewsBindings(state) at the end of your existing
 * init() / bindEvents() function in main.js.
 *
 * All click-handling follows the existing event-delegation pattern
 * already used in the portal (data-action attributes).
 */

import {
  showNews,
  refreshNews,
  loadMoreNews,
  setNewsRegionFilter,
  setNewsDocTypeFilter,
  handleNewsSearch,
  openNewsPreview,
  askKiliAboutArticle,
  teardownNews,
} from './handlers-news';
import { closeNewsPreview } from './ui-renderers-news';

/**
 * Wire all news-segment interactions.
 *
 * @param {Object} state  — central assistant state (with newsState merged in)
 */
export function initNewsBindings(state) {
  /* ── 1. Header News button ── */
  _on('click', '#ai-news-btn', () => showNews(state));

  /* ── 2. Refresh button ── */
  _on('click', '#ai-news-refresh-btn', () => refreshNews(state));

  /* ── 3. Retry button (error state) ── */
  _on('click', '#ai-news-retry-btn', () => refreshNews(state));

  /* ── 4. Reset filters (empty state) ── */
  _on('click', '#ai-news-empty-reset', () => {
    _resetAllFilters(state);
    refreshNews(state);
  });

  /* ── 5. Filter pills (region + doctype) — event delegation ── */
  _on('click', '#ai-news-filters', e => {
    const pill = e.target.closest('.ai-news-filter-pill');
    if (!pill) return;
    e.stopPropagation();

    const group = pill.dataset.group;
    if (group === 'region') {
      setNewsRegionFilter(state, pill.dataset.region);
    } else if (group === 'doctype') {
      setNewsDocTypeFilter(state, pill.dataset.doctype);
    }
  });

  /* ── 6. Search input ── */
  _on('input', '#ai-news-search', e => {
    handleNewsSearch(state, e.target.value);
  });

  _on('keydown', '#ai-news-search', e => {
    if (e.key === 'Escape') {
      e.target.value = '';
      handleNewsSearch(state, '');
      document.getElementById('ai-news-search-clear').style.display = 'none';
    }
  });

  _on('click', '#ai-news-search-clear', () => {
    const input = document.getElementById('ai-news-search') as HTMLInputElement | null;
    if (input) input.value = '';
    handleNewsSearch(state, '');
    document.getElementById('ai-news-search-clear').style.display = 'none';
  });

  /* ── 7. Article card click → open inline preview ── */
  _on('click', '#ai-news-list', async e => {
    /* Let external-link <a> pass through without opening preview */
    if (e.target.closest('[data-action="news-open-external"]')) return;

    /* "Ask Kili" button inside a card */
    const askBtn = e.target.closest('[data-action="news-ask-kili"]');
    if (askBtn) {
      e.stopPropagation();
      askKiliAboutArticle(state, askBtn.dataset.title ?? '');
      return;
    }

    /* Card itself or any non-action child */
    const card = e.target.closest('.ai-news-card');
    if (card) {
      await openNewsPreview(state, card.dataset.id);
    }
  });

  /* Keyboard accessibility on cards */
  _on('keydown', '#ai-news-list', e => {
    if (e.key === 'Enter' || e.key === ' ') {
      const card = e.target.closest('.ai-news-card');
      if (card) {
        e.preventDefault();
        openNewsPreview(state, card.dataset.id);
      }
    }
  });

  /* ── 8. Load more ── */
  _on('click', '#ai-news-load-more', () => loadMoreNews(state));

  /* ── 9. Preview: back button ── */
  _on('click', '#ai-news-preview-back', () => {
    closeNewsPreview();
    state.newsPreviewArticle = null;
  });

  /* ── 10. Preview: "Ask Kili about this" ── */
  _on('click', '#ai-news-ask-kili', () => {
    const article = state.newsPreviewArticle;
    if (article) askKiliAboutArticle(state, article.title);
  });

  /* ── 11. Swipe-down to close preview (touch) ── */
  _addSwipeClose('#ai-news-preview', () => {
    closeNewsPreview();
    state.newsPreviewArticle = null;
  });

  /* ── 12. Teardown when widget closes ── */
  const widgetRoot = document.getElementById('akili-assistant-widget') ||
                     document.getElementById('ai-widget');
  if (widgetRoot) {
    widgetRoot.addEventListener('widget:close', () => teardownNews(state));
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Private helpers
─────────────────────────────────────────────────────────────────────────────── */

/**
 * Delegated event listener helper.
 * @param {string} event
 * @param {string} selector
 * @param {Function} handler
 */
function _on(event, selector, handler) {
  /* Try direct element first (for focus/input events) */
  const el = document.querySelector(selector);
  if (el && (event === 'input' || event === 'keydown' || event === 'focus')) {
    el.addEventListener(event, handler);
    return;
  }

  /* Otherwise delegate from document */
  document.addEventListener(event, e => {
    const target = e.target.closest ? e.target.closest(selector) : null;
    /* For non-delegatable events on specific elements */
    if (target || (el && (el === e.target || el.contains(e.target)))) {
      handler(e);
    }
  });
}

function _resetAllFilters(state) {
  state.newsFilters.region  = 'all';
  state.newsFilters.docType = 'all';
  state.newsFilters.search  = '';

  document.querySelectorAll('.ai-news-filter-pill').forEach(p => {
    const pill = p as HTMLElement;
    const isAllRegion  = pill.dataset.group === 'region'  && pill.dataset.region  === 'all';
    const isAllDoctype = pill.dataset.group === 'doctype' && pill.dataset.doctype === 'all';
    pill.classList.toggle('active', isAllRegion || isAllDoctype);
  });

  const searchInput = document.getElementById('ai-news-search') as HTMLInputElement | null;
  if (searchInput) searchInput.value = '';
  const clearBtn = document.getElementById('ai-news-search-clear');
  if (clearBtn) clearBtn.style.display = 'none';
}

/**
 * Minimal swipe-down gesture handler to close a panel.
 * @param {string}   selector
 * @param {Function} onClose
 */
function _addSwipeClose(selector, onClose) {
  const el = document.querySelector(selector);
  if (!el) return;

  let startY = null;
  el.addEventListener('touchstart', e => {
    startY = e.touches[0].clientY;
  }, { passive: true });

  el.addEventListener('touchend', e => {
    if (startY === null) return;
    const dy = e.changedTouches[0].clientY - startY;
    startY = null;
    if (dy > 80) onClose(); // 80 px downward swipe
  }, { passive: true });
}
